import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Title,
  Tooltip as ChartTooltip
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import moment from "moment";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";

// ESTRATIZA-DASHBOARD-V2-LAYOUT-20261003: versão visual da marca Estratiza.
ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Title,
  ChartTooltip
);

const CLIENT_COLOR = "#12B76A";
const COMPANY_COLOR = "#2E90FA";
const MUTED_COLOR = "#98A2B3";
const WAITING_COLOR = "#F79009";
const FONT_FAMILY = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "last7", label: "7 dias" },
  { key: "last30", label: "30 dias" },
  { key: "month", label: "Este mês" },
  { key: "previousMonth", label: "Mês passado" },
  { key: "all", label: "Todo o período" },
  { key: "custom", label: "Personalizado" }
];

const emptyData = {
  live: { waiting: 0, attending: 0, online: 0 },
  totals: {
    clientStarted: 0,
    companyStarted: 0,
    verifiedOrigins: 0,
    excludedUnclassified: 0
  },
  leads7Days: { period: {}, points: [] },
  messageVolume: { interval: "day", received: 0, sent: 0, points: [] },
  byUser: [],
  byQueue: [],
  demandByHour: []
};

const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === "dark";
  const surface = dark ? "#141A17" : "#FFFFFF";
  const border = dark ? "#232B27" : "#E7ECE9";
  const subtle = dark ? "#1B231F" : "#F2F5F3";
  const shadow = dark
    ? "0 1px 2px rgba(0,0,0,.4)"
    : "0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)";
  return {
    root: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      padding: "24px 24px 16px",
      background: dark ? "#0C110F" : "#F5F7F6",
      fontFamily: FONT_FAMILY,
      ...theme.scrollbarStyles,
      "@media(max-width:600px)": { padding: "16px 12px" }
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      gap: 16,
      flexWrap: "wrap",
      marginBottom: 20
    },
    title: {
      fontSize: 26,
      fontWeight: 700,
      letterSpacing: "-.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.2
    },
    meta: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      marginTop: 6,
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap"
    },
    metaDot: {
      width: 3,
      height: 3,
      borderRadius: "50%",
      background: theme.palette.text.secondary,
      opacity: 0.6
    },
    controls: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      maxWidth: "100%"
    },
    segmented: {
      display: "flex",
      padding: 3,
      borderRadius: 10,
      background: dark ? "#1B231F" : "#EAEEEC",
      overflowX: "auto",
      maxWidth: "100%",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" }
    },
    segment: {
      border: 0,
      background: "transparent",
      cursor: "pointer",
      whiteSpace: "nowrap",
      padding: "7px 12px",
      borderRadius: 8,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      transition: "background .15s ease, color .15s ease, box-shadow .15s ease",
      "&:hover": { color: theme.palette.text.primary },
      "&:focus-visible": { outline: `2px solid ${CLIENT_COLOR}`, outlineOffset: 1 }
    },
    segmentActive: {
      background: surface,
      color: theme.palette.text.primary,
      fontWeight: 600,
      boxShadow: dark ? "0 1px 2px rgba(0,0,0,.5)" : "0 1px 2px rgba(16,24,40,.08), 0 1px 3px rgba(16,24,40,.08)"
    },
    refreshButton: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: 13,
      borderRadius: 10,
      padding: "6px 14px",
      background: surface,
      borderColor: border,
      color: theme.palette.text.primary,
      boxShadow: shadow,
      "&:hover": { background: subtle, borderColor: border }
    },
    custom: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      alignItems: "center",
      margin: "-6px 0 20px",
      padding: "12px 14px",
      borderRadius: 14,
      background: surface,
      border: `1px solid ${border}`,
      boxShadow: shadow,
      fontSize: 13,
      color: theme.palette.text.secondary
    },
    customInput: { background: surface, borderRadius: 8 },
    applyButton: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      background: CLIENT_COLOR,
      color: "#fff",
      boxShadow: "none",
      "&:hover": { background: "#0E9F5C", boxShadow: "none" }
    },
    warning: {
      display: "flex",
      gap: 10,
      alignItems: "center",
      padding: "12px 16px",
      borderRadius: 12,
      marginBottom: 16,
      background: dark ? "#2A1F0B" : "#FFFAEB",
      border: `1px solid ${dark ? "#4D3A12" : "#FEDF89"}`,
      color: dark ? "#FEC84B" : "#93370D",
      fontSize: 13
    },
    error: {
      padding: "12px 16px",
      borderRadius: 12,
      color: dark ? "#FDA29B" : "#B42318",
      background: dark ? "#2D1412" : "#FEF3F2",
      border: `1px solid ${dark ? "#55201B" : "#FECDCA"}`,
      marginBottom: 16,
      fontSize: 13
    },
    cards: {
      display: "grid",
      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      gap: 16,
      marginBottom: 16,
      "@media(max-width:900px)": { gridTemplateColumns: "1fr" }
    },
    kpi: {
      padding: "20px 22px",
      borderRadius: 16,
      border: `1px solid ${border}`,
      background: surface,
      boxShadow: shadow,
      display: "flex",
      flexDirection: "column",
      gap: 14
    },
    kpiTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    kpiIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": { fontSize: 21 }
    },
    kpiNow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: ".04em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: "4px 9px",
      borderRadius: 99,
      background: subtle
    },
    kpiNowDot: { width: 6, height: 6, borderRadius: "50%", background: CLIENT_COLOR },
    kpiLabel: { fontSize: 13, fontWeight: 500, color: theme.palette.text.secondary },
    kpiValue: {
      fontSize: 34,
      fontWeight: 700,
      letterSpacing: "-.03em",
      lineHeight: 1.05,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
      marginTop: 4
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(12,minmax(0,1fr))",
      gap: 16
    },
    span8: { gridColumn: "span 8", "@media(max-width:1200px)": { gridColumn: "span 12" } },
    span4: { gridColumn: "span 4", "@media(max-width:1200px)": { gridColumn: "span 12" } },
    span6: { gridColumn: "span 6", "@media(max-width:1000px)": { gridColumn: "span 12" } },
    panel: {
      padding: "20px 22px",
      borderRadius: 16,
      border: `1px solid ${border}`,
      background: surface,
      boxShadow: shadow,
      display: "flex",
      flexDirection: "column",
      minWidth: 0
    },
    panelHead: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
      flexWrap: "wrap",
      marginBottom: 16
    },
    panelTitleRow: { display: "flex", alignItems: "center", gap: 6 },
    panelTitle: { fontSize: 15, fontWeight: 600, color: theme.palette.text.primary },
    panelHint: {
      fontSize: 12.5,
      lineHeight: 1.45,
      color: theme.palette.text.secondary,
      marginTop: 4,
      maxWidth: 560
    },
    infoIcon: { fontSize: 16, color: theme.palette.text.secondary, opacity: 0.8, cursor: "help" },
    chart: { height: 260, position: "relative" },
    chartTall: { height: 280, position: "relative" },
    legendRow: { display: "flex", gap: 18, flexWrap: "wrap" },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12.5,
      color: theme.palette.text.secondary
    },
    legendValue: {
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums"
    },
    dot: { width: 8, height: 8, borderRadius: "50%", flex: "0 0 auto" },
    donutWrap: { height: 200, position: "relative", margin: "4px 0 18px" },
    originList: { display: "flex", flexDirection: "column", gap: 10, marginTop: "auto" },
    originRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      padding: "10px 12px",
      borderRadius: 12,
      background: subtle
    },
    originName: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: theme.palette.text.primary },
    originValue: {
      fontSize: 15,
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums"
    },
    originPct: { fontSize: 12, fontWeight: 500, color: theme.palette.text.secondary, marginLeft: 6 },
    rankList: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
      maxHeight: 300,
      overflowY: "auto",
      paddingRight: 4,
      ...theme.scrollbarStyles
    },
    rankRow: { display: "flex", flexDirection: "column", gap: 7 },
    rankTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
    rankName: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.primary
    },
    rankNameText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    avatar: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      flex: "0 0 auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: COMPANY_COLOR,
      background: dark ? "rgba(46,144,250,.16)" : "#EFF8FF"
    },
    rankValue: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap"
    },
    rankPct: { fontWeight: 500, color: theme.palette.text.secondary, marginLeft: 6 },
    track: { height: 8, borderRadius: 99, background: subtle, overflow: "hidden" },
    fill: { height: "100%", borderRadius: 99, transition: "width .4s ease" },
    empty: {
      minHeight: 200,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.secondary,
      fontSize: 13,
      textAlign: "center",
      padding: 20,
      boxSizing: "border-box",
      borderRadius: 12,
      background: subtle
    },
    center: { display: "flex", minHeight: 320, alignItems: "center", justifyContent: "center" }
  };
});

const boundsFor = key => {
  const today = moment();
  if (key === "today") return [today.clone(), today.clone()];
  if (key === "last7") return [today.clone().subtract(6, "days"), today.clone()];
  if (key === "month") return [today.clone().startOf("month"), today.clone()];
  if (key === "previousMonth") {
    return [
      today.clone().subtract(1, "month").startOf("month"),
      today.clone().subtract(1, "month").endOf("month")
    ];
  }
  return [today.clone().subtract(29, "days"), today.clone()];
};

const hasValues = values => values.some(value => Number(value) > 0);

const initialsOf = name => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return `${first}${last}`.toUpperCase();
};

const formatNumber = value => Number(value || 0).toLocaleString("pt-BR");

const PremiumDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const initial = boundsFor("last30");
  const [period, setPeriod] = useState("last30");
  const [customFrom, setCustomFrom] = useState(initial[0].format("YYYY-MM-DD"));
  const [customTo, setCustomTo] = useState(initial[1].format("YYYY-MM-DD"));
  const [appliedCustom, setAppliedCustom] = useState({
    from: initial[0].format("YYYY-MM-DD"),
    to: initial[1].format("YYYY-MM-DD")
  });
  const [data, setData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [tokenWarning, setTokenWarning] = useState(null);

  const loadSelection = useCallback(async (selection, firstLoad = false) => {
    if (firstLoad) setInitialLoading(true);
    else setRefreshing(true);
    setError("");
    try {
      const params = selection.key === "all" ? { all: true } : {};
      if (selection.key !== "all") {
        const [start, end] = selection.key === "custom"
          ? [moment(selection.from), moment(selection.to)]
          : boundsFor(selection.key);
        if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
          throw new Error("Período personalizado inválido");
        }
        params.dateFrom = start.format("YYYY-MM-DD");
        params.dateTo = end.format("YYYY-MM-DD");
      }
      const response = await api.get("/dashboard/premium", { params });
      setData(response.data || emptyData);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err.message ||
        "Não foi possível carregar o dashboard"
      );
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSelection({ key: "last30" }, true);
  }, [loadSelection]);

  useEffect(() => {
    api.get("/companiesPlan").then(({ data: planData }) => {
      const limit = planData?.plan?.aiTokensLimit || 0;
      if (!limit) return;
      api.get(`/companies/${planData.id}`).then(({ data: company }) => {
        const pct = Math.round(((company?.aiTokensUsed || 0) / limit) * 100);
        if (pct >= 90) setTokenWarning({ pct });
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  const handlePeriod = key => {
    setPeriod(key);
    if (key !== "custom") loadSelection({ key });
  };

  const applyCustom = () => {
    setAppliedCustom({ from: customFrom, to: customTo });
    loadSelection({ key: "custom", from: customFrom, to: customTo });
  };

  const refresh = () => {
    if (period === "custom") {
      loadSelection({ key: "custom", ...appliedCustom });
      return;
    }
    loadSelection({ key: period });
  };

  const view = data || emptyData;
  const dark = theme.palette.type === "dark";
  const axisColor = dark ? "#8A968F" : "#667085";
  const gridColor = dark ? "rgba(255,255,255,.06)" : "rgba(16,24,40,.06)";
  const primaryText = theme.palette.text.primary;
  const surfaceColor = dark ? "#141A17" : "#FFFFFF";

  const baseOptions = (extra = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    interaction: { mode: "index", intersect: false },
    layout: { padding: { top: 6 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: dark ? "#F2F5F3" : "#101828",
        titleColor: dark ? "#101828" : "#FFFFFF",
        bodyColor: dark ? "#344054" : "#E4E7EC",
        titleFont: { family: FONT_FAMILY, size: 12, weight: "600" },
        bodyFont: { family: FONT_FAMILY, size: 12 },
        padding: 10,
        cornerRadius: 10,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        usePointStyle: true
      }
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: axisColor,
          font: { family: FONT_FAMILY, size: 11 },
          autoSkip: true,
          maxRotation: 0,
          padding: 6
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor, drawBorder: false, drawTicks: false },
        ticks: {
          color: axisColor,
          font: { family: FONT_FAMILY, size: 11 },
          precision: 0,
          maxTicksLimit: 5,
          padding: 8
        }
      }
    },
    ...extra
  });

  // Rotulo de valor no topo das barras (somente desenho, sem alterar dados).
  const valueLabelsPlugin = (topOnly = 0) => ({
    id: "premiumValueLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      ctx.save();
      ctx.font = `600 11px ${FONT_FAMILY}`;
      ctx.fillStyle = primaryText;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta.hidden) return;
        // Le os valores atuais do grafico a cada desenho (troca de periodo).
        const allowed = topOnly
          ? new Set(dataset.data
            .map((value, index) => ({ value: Number(value || 0), index }))
            .filter(item => item.value > 0)
            .sort((x, y) => y.value - x.value || x.index - y.index)
            .slice(0, topOnly)
            .map(item => item.index))
          : null;
        meta.data.forEach((element, index) => {
          const value = Number(dataset.data[index] || 0);
          if (!value) return;
          if (allowed && !allowed.has(index)) return;
          ctx.fillText(formatNumber(value), element.x, element.y - 5);
        });
      });
      ctx.restore();
    }
  });

  const queueData = useMemo(() => [...view.byQueue].sort((a, b) => Number(b.count) - Number(a.count)), [view.byQueue]);
  const userRanking = useMemo(() => [...view.byUser].sort((a, b) => Number(b.count) - Number(a.count)), [view.byUser]);

  const originData = useMemo(() => ({
    labels: ["Iniciados pelo cliente", "Iniciados pela empresa"],
    datasets: [{
      data: [view.totals.clientStarted, view.totals.companyStarted],
      backgroundColor: [CLIENT_COLOR, COMPANY_COLOR],
      borderColor: surfaceColor,
      borderWidth: 3,
      hoverOffset: 4,
      spacing: 2
    }]
  }), [view.totals.clientStarted, view.totals.companyStarted, surfaceColor]);

  const leadPoints = useMemo(() => {
    const start = view.leads7Days?.period?.from;
    if (!start) return [];
    const lookup = new Map(view.leads7Days.points.map(item => [item.day, item.count]));
    return Array.from({ length: 7 }, (_, index) => {
      const day = moment.parseZone(start).add(index, "days");
      const key = day.format("YYYY-MM-DD");
      return { day: key, label: day.format("DD/MM"), count: lookup.get(key) || 0 };
    });
  }, [view.leads7Days]);

  const leadsData = useMemo(() => ({
    labels: leadPoints.map(item => item.label),
    datasets: [{
      label: "Novos leads",
      data: leadPoints.map(item => item.count),
      backgroundColor: CLIENT_COLOR,
      hoverBackgroundColor: "#0E9F5C",
      borderRadius: 8,
      borderSkipped: false,
      maxBarThickness: 34
    }]
  }), [leadPoints]);

  const areaGradient = (color, alphaTop, alphaBottom) => context => {
    const { chart } = context;
    const { ctx, chartArea } = chart;
    if (!chartArea) return color;
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    gradient.addColorStop(0, `rgba(${r},${g},${b},${alphaTop})`);
    gradient.addColorStop(1, `rgba(${r},${g},${b},${alphaBottom})`);
    return gradient;
  };

  const singlePoint = view.messageVolume.points.length <= 1;
  const messageData = useMemo(() => ({
    labels: view.messageVolume.points.map(item => {
      if (view.messageVolume.interval === "hour") return item.bucket;
      if (view.messageVolume.interval === "month") return moment(`${item.bucket}-01`).format("MM/YYYY");
      return moment(item.bucket).isValid() ? moment(item.bucket).format("DD/MM") : item.bucket;
    }),
    datasets: [
      {
        label: "Recebidas dos clientes",
        data: view.messageVolume.points.map(item => item.received),
        borderColor: CLIENT_COLOR,
        backgroundColor: areaGradient(CLIENT_COLOR, 0.22, 0),
        pointBackgroundColor: CLIENT_COLOR,
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: singlePoint ? 4 : 0,
        pointHoverRadius: 5
      },
      {
        label: "Enviadas pela empresa",
        data: view.messageVolume.points.map(item => item.sent),
        borderColor: COMPANY_COLOR,
        backgroundColor: areaGradient(COMPANY_COLOR, 0.18, 0),
        pointBackgroundColor: COMPANY_COLOR,
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: singlePoint ? 4 : 0,
        pointHoverRadius: 5
      }
    ]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [view.messageVolume, singlePoint]);

  const demand = view.demandByHour.length
    ? view.demandByHour
    : Array.from({ length: 24 }, (_, hour) => ({ hour, count: 0 }));
  const topHours = useMemo(() => new Set(
    [...demand]
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count || a.hour - b.hour)
      .slice(0, 3)
      .map(item => item.hour)
  ), [demand]);
  const demandData = useMemo(() => ({
    labels: demand.map(item => `${String(item.hour).padStart(2, "0")}h`),
    datasets: [{
      label: "Atendimentos iniciados",
      data: demand.map(item => item.count),
      backgroundColor: demand.map(item => topHours.has(item.hour) ? CLIENT_COLOR : (dark ? "rgba(18,183,106,.28)" : "rgba(18,183,106,.22)")),
      hoverBackgroundColor: demand.map(item => topHours.has(item.hour) ? "#0E9F5C" : "rgba(18,183,106,.45)"),
      borderRadius: 6,
      borderSkipped: false,
      maxBarThickness: 26
    }]
  }), [demand, topHours, dark]);

  const periodLabel = view.period?.from
    ? `${moment(view.period.from).format("DD/MM/YYYY")} – ${moment(view.period.to).format("DD/MM/YYYY")}`
    : "—";
  const updatedLabel = view.generatedAt ? moment(view.generatedAt).format("HH:mm") : null;
  const originValues = [view.totals.clientStarted, view.totals.companyStarted];
  const originTotal = Number(view.totals.clientStarted || 0) + Number(view.totals.companyStarted || 0);
  const messageValues = view.messageVolume.points.flatMap(item => [item.received, item.sent]);
  const cards = [
    {
      label: "Aguardando agora",
      value: view.live.waiting,
      color: WAITING_COLOR,
      tint: dark ? "rgba(247,144,9,.16)" : "#FFFAEB",
      icon: <HourglassEmptyIcon />
    },
    {
      label: "Em atendimento agora",
      value: view.live.attending,
      color: COMPANY_COLOR,
      tint: dark ? "rgba(46,144,250,.16)" : "#EFF8FF",
      icon: <RecordVoiceOverIcon />
    },
    {
      label: "Atendentes online agora",
      value: view.live.online,
      color: CLIENT_COLOR,
      tint: dark ? "rgba(18,183,106,.16)" : "#ECFDF3",
      icon: <PeopleAltIcon />
    }
  ];

  if (user.showDashboard !== "enabled") return <Redirect to="/tickets" />;

  const emptyChart = <div className={classes.empty}>Não há dados comprovados neste período.</div>;
  const originTooltip = context => {
    const total = view.totals.verifiedOrigins || 0;
    const value = Number(context.raw || 0);
    const pct = total ? ((value / total) * 100).toFixed(1) : "0.0";
    return `${context.label}: ${value} (${pct}%)`;
  };
  const pctOf = (value, total) => (total ? `${((Number(value || 0) / total) * 100).toFixed(1).replace(".", ",")}%` : "0%");

  const donutCenterPlugin = {
    id: "premiumDonutCenter",
    afterDraw(chart) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const x = (chartArea.left + chartArea.right) / 2;
      const y = (chartArea.top + chartArea.bottom) / 2;
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = primaryText;
      ctx.font = `700 26px ${FONT_FAMILY}`;
      const total = (chart.data.datasets[0]?.data || []).reduce((sum, value) => sum + Number(value || 0), 0);
      ctx.fillText(formatNumber(total), x, y - 7);
      ctx.fillStyle = axisColor;
      ctx.font = `500 11px ${FONT_FAMILY}`;
      ctx.fillText("atendimentos", x, y + 15);
      ctx.restore();
    }
  };

  const renderRanking = (items, { avatar }) => {
    const max = Math.max(...items.map(item => Number(item.count) || 0), 1);
    const total = items.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    return <div className={classes.rankList}>
      {items.map((item, index) => {
        const color = avatar ? COMPANY_COLOR : (item.color || MUTED_COLOR);
        return <div className={classes.rankRow} key={`${item.name}-${index}`}>
          <div className={classes.rankTop}>
            <div className={classes.rankName}>
              {avatar
                ? <span className={classes.avatar}>{initialsOf(item.name)}</span>
                : <span className={classes.dot} style={{ background: color }} />}
              <Tooltip title={item.name || ""}><span className={classes.rankNameText}>{item.name}</span></Tooltip>
            </div>
            <span className={classes.rankValue}>
              {formatNumber(item.count)}
              <span className={classes.rankPct}>{pctOf(item.count, total)}</span>
            </span>
          </div>
          <div className={classes.track}>
            <div className={classes.fill} style={{ width: `${Math.max(2, (Number(item.count) / max) * 100)}%`, background: color }} />
          </div>
        </div>;
      })}
    </div>;
  };

  return <MainContainer><Paper elevation={0} className={classes.root}>
    {tokenWarning && <div className={classes.warning}>
      <InfoOutlinedIcon fontSize="small" />
      <span><b>Limite de IA quase esgotado — {tokenWarning.pct}% consumido.</b> Entre em contato com o suporte para garantir continuidade.</span>
    </div>}

    <header className={classes.header}>
      <div>
        <Typography component="h1" className={classes.title}>Visão Operacional</Typography>
        <div className={classes.meta}>
          <span>{periodLabel}</span>
          {updatedLabel && <><span className={classes.metaDot} /><span>Atualizado às {updatedLabel}</span></>}
        </div>
      </div>
      <div className={classes.controls}>
        <div className={classes.segmented} role="tablist" aria-label="Período">
          {PERIODS.map(item => <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={period === item.key}
            onClick={() => handlePeriod(item.key)}
            className={`${classes.segment} ${period === item.key ? classes.segmentActive : ""}`}
          >
            {item.label}
          </button>)}
        </div>
        <Button
          variant="outlined"
          onClick={refresh}
          disabled={refreshing || initialLoading}
          startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
          className={classes.refreshButton}
        >
          {refreshing ? "Atualizando" : "Atualizar"}
        </Button>
      </div>
    </header>

    {period === "custom" && <div className={classes.custom}>
      <span>De</span>
      <TextField className={classes.customInput} size="small" variant="outlined" type="date" value={customFrom} onChange={event => setCustomFrom(event.target.value)} />
      <span>até</span>
      <TextField className={classes.customInput} size="small" variant="outlined" type="date" value={customTo} onChange={event => setCustomTo(event.target.value)} />
      <Button variant="contained" onClick={applyCustom} disabled={refreshing} className={classes.applyButton}>Aplicar</Button>
    </div>}

    {error && <div className={classes.error}>{error}</div>}
    {initialLoading && !data ? <div className={classes.center}><CircularProgress style={{ color: CLIENT_COLOR }} /></div> : <>
      <div className={classes.cards}>
        {cards.map(card => <div className={classes.kpi} key={card.label}>
          <div className={classes.kpiTop}>
            <span className={classes.kpiIcon} style={{ background: card.tint, color: card.color }}>{card.icon}</span>
            <span className={classes.kpiNow}><i className={classes.kpiNowDot} />Agora{updatedLabel ? ` · ${updatedLabel}` : ""}</span>
          </div>
          <div>
            <Typography className={classes.kpiLabel}>{card.label}</Typography>
            <Typography className={classes.kpiValue}>{formatNumber(card.value)}</Typography>
          </div>
        </div>)}
      </div>

      <div className={classes.grid}>
        <section className={`${classes.panel} ${classes.span8}`}>
          <div className={classes.panelHead}>
            <div>
              <Typography className={classes.panelTitle}>Volume de mensagens</Typography>
              <Typography className={classes.panelHint}>Compare quantas mensagens sua empresa recebeu e enviou no período selecionado.</Typography>
            </div>
            <div className={classes.legendRow}>
              <span className={classes.legendItem}><i className={classes.dot} style={{ background: CLIENT_COLOR }} />Recebidas <span className={classes.legendValue}>{formatNumber(view.messageVolume.received)}</span></span>
              <span className={classes.legendItem}><i className={classes.dot} style={{ background: COMPANY_COLOR }} />Enviadas <span className={classes.legendValue}>{formatNumber(view.messageVolume.sent)}</span></span>
            </div>
          </div>
          <div className={classes.chartTall}>
            {hasValues(messageValues) ? <Line data={messageData} options={baseOptions()} /> : emptyChart}
          </div>
        </section>

        <section className={`${classes.panel} ${classes.span4}`}>
          <div className={classes.panelHead}>
            <div>
              <div className={classes.panelTitleRow}>
                <Typography className={classes.panelTitle}>Origem dos atendimentos</Typography>
                <Tooltip title={`A divisão usa somente origens comprovadas. ${view.totals.excludedUnclassified || 0} registro(s) antigo(s) sem comprovação ficaram fora da base.`}>
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </Tooltip>
              </div>
              <Typography className={classes.panelHint}>Atendimentos iniciados no período, incluindo novos contatos e clientes antigos que voltaram a conversar. Base comprovada: {formatNumber(view.totals.verifiedOrigins)}.</Typography>
            </div>
          </div>
          {hasValues(originValues) ? <>
            <div className={classes.donutWrap}>
              <Doughnut
                data={originData}
                plugins={[donutCenterPlugin]}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: "74%",
                  animation: { duration: 500 },
                  plugins: {
                    legend: { display: false },
                    tooltip: { ...baseOptions().plugins.tooltip, callbacks: { label: originTooltip } }
                  }
                }}
              />
            </div>
            <div className={classes.originList}>
              <div className={classes.originRow}>
                <span className={classes.originName}><i className={classes.dot} style={{ background: CLIENT_COLOR }} />Iniciados pelo cliente</span>
                <span className={classes.originValue}>{formatNumber(view.totals.clientStarted)}<span className={classes.originPct}>{pctOf(view.totals.clientStarted, originTotal)}</span></span>
              </div>
              <div className={classes.originRow}>
                <span className={classes.originName}><i className={classes.dot} style={{ background: COMPANY_COLOR }} />Iniciados pela empresa</span>
                <span className={classes.originValue}>{formatNumber(view.totals.companyStarted)}<span className={classes.originPct}>{pctOf(view.totals.companyStarted, originTotal)}</span></span>
              </div>
            </div>
          </> : emptyChart}
        </section>

        <section className={`${classes.panel} ${classes.span8}`}>
          <div className={classes.panelHead}>
            <div>
              <Typography className={classes.panelTitle}>Horários de maior procura</Typography>
              <Typography className={classes.panelHint}>Veja em quais horários seus clientes mais iniciam conversas e identifique os períodos de maior demanda da equipe.</Typography>
            </div>
            <div className={classes.legendRow}>
              <span className={classes.legendItem}><i className={classes.dot} style={{ background: CLIENT_COLOR }} />3 maiores horários</span>
            </div>
          </div>
          <div className={classes.chart}>
            {hasValues(demand.map(item => item.count))
              ? <Bar
                data={demandData}
                plugins={[valueLabelsPlugin(3)]}
                options={baseOptions({
                  layout: { padding: { top: 18 } },
                  interaction: { mode: "nearest", intersect: true },
                  plugins: {
                    ...baseOptions().plugins,
                    tooltip: {
                      ...baseOptions().plugins.tooltip,
                      displayColors: false,
                      callbacks: { label: context => `${context.label} — ${context.raw} atendimento(s) iniciado(s)` }
                    }
                  }
                })}
              />
              : emptyChart}
          </div>
        </section>

        <section className={`${classes.panel} ${classes.span4}`}>
          <div className={classes.panelHead}>
            <div>
              <Typography className={classes.panelTitle}>Controle de Leads</Typography>
              <Typography className={classes.panelHint}>Novos contatos que chamaram sua empresa nos últimos 7 dias. Este gráfico possui período próprio e não acompanha o filtro superior.</Typography>
            </div>
          </div>
          <div className={classes.chart}>
            {hasValues(leadPoints.map(item => item.count))
              ? <Bar
                data={leadsData}
                plugins={[valueLabelsPlugin()]}
                options={baseOptions({ layout: { padding: { top: 18 } } })}
              />
              : emptyChart}
          </div>
        </section>

        <section className={`${classes.panel} ${classes.span6}`}>
          <div className={classes.panelHead}>
            <div>
              <Typography className={classes.panelTitle}>Atendimentos assumidos por usuário</Typography>
              <Typography className={classes.panelHint}>Quantidade de ciclos de atendimento efetivamente aceitos por cada atendente no período.</Typography>
            </div>
          </div>
          {hasValues(userRanking.map(item => item.count)) ? renderRanking(userRanking, { avatar: true }) : emptyChart}
        </section>

        <section className={`${classes.panel} ${classes.span6}`}>
          <div className={classes.panelHead}>
            <div>
              <Typography className={classes.panelTitle}>Atendimentos por fila</Typography>
              <Typography className={classes.panelHint}>Distribuição de todos os atendimentos iniciados entre as respectivas filas no período selecionado. A soma corresponde ao total do período.</Typography>
            </div>
          </div>
          {hasValues(queueData.map(item => item.count)) ? renderRanking(queueData, { avatar: false }) : emptyChart}
        </section>
      </div>
    </>}
    <Box height={8} />
  </Paper></MainContainer>;
};

export default PremiumDashboard;
